import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Plus, Trash2, MapPin, Hospital, Star } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

export function EstablishmentManager({ onChange, reloadSignal }) {
  const [list, setList] = useState([]);
  const [addr, setAddr] = useState("");
  const [radius, setRadius] = useState(15);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [manual, setManual] = useState({ name: "", address: "" });
  const [manualCoords, setManualCoords] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/establishments");
      setList(data);
      onChange?.(data);
    } catch (err) { /* silencieux */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (reloadSignal !== undefined) load(); }, [reloadSignal]); // eslint-disable-line

  const search = async () => {
    setSearching(true);
    setSuggestions([]);
    try {
      let url = `/establishments/suggest?radius_km=${Number(radius) || 15}`;
      if (addr.trim()) url += `&address=${encodeURIComponent(addr.trim())}`;
      const { data } = await api.get(url);
      setSuggestions(data.results || []);
      if (!data.results?.length) toast.message("Aucun établissement trouvé", { description: "Élargissez le rayon ou ajoutez manuellement." });
    } catch (err) { toast.error(apiError(err)); }
    finally { setSearching(false); }
  };

  const addFromSuggestion = async (s) => {
    setBusy(true);
    try {
      await api.post("/establishments", { name: s.name, address: s.address, lat: s.lat, lon: s.lon, kind: s.kind });
      toast.success(`« ${s.name} » ajouté`);
      setSuggestions((prev) => prev.map((x) => x.name === s.name ? { ...x, already_added: true } : x));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const addManual = async () => {
    if (!manual.name.trim()) { toast.error("Nom requis"); return; }
    setBusy(true);
    try {
      const payload = { name: manual.name.trim(), address: manual.address.trim() };
      if (manualCoords) { payload.lat = manualCoords.lat; payload.lon = manualCoords.lon; }
      await api.post("/establishments", payload);
      toast.success(`« ${manual.name.trim()} » ajouté`);
      setManual({ name: "", address: "" });
      setManualCoords(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/establishments/${id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const toggleFav = async (e) => {
    try {
      await api.post(`/establishments/${e.id}/favorite`);
      toast.success(e.favorite ? "Favori retiré" : `« ${e.name} » défini comme favori (pré-sélectionné dans Jeregul)`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="establishment-manager">
      {/* Recherche autour */}
      <div className="space-y-2">
        <Label className="text-xs font-medium flex items-center gap-1"><Search className="h-3.5 w-3.5" /> Rechercher autour d'une adresse</Label>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Adresse (vide = adresse de la société)" value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="estab-search-address" />
          </div>
          <div className="w-24">
            <Label className="text-[11px] text-muted-foreground">Rayon (km)</Label>
            <Input type="number" min="1" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="estab-search-radius" />
          </div>
          <Button onClick={search} disabled={searching} data-testid="estab-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Rechercher autour de moi
          </Button>
        </div>
        {suggestions.length > 0 && (
          <div className="border border-border rounded-md divide-y max-h-64 overflow-auto" data-testid="estab-suggestions">
            {suggestions.map((s) => (
              <div key={s.name} className="flex items-center justify-between gap-2 p-2 text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <Hospital className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="truncate"><b>{s.name}</b> <span className="text-muted-foreground text-xs">· {s.kind}{s.address ? ` · ${s.address}` : ""}</span></span>
                </span>
                {s.already_added
                  ? <span className="text-xs text-muted-foreground shrink-0">Ajouté ✓</span>
                  : <Button size="sm" variant="secondary" disabled={busy} onClick={() => addFromSuggestion(s)} data-testid={`estab-add-suggestion`}><Plus className="h-3.5 w-3.5" /></Button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ajout manuel */}
      <div className="space-y-2 border-t border-border pt-4">
        <Label className="text-xs font-medium flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Ajouter manuellement</Label>
        <div className="space-y-2">
          <div>
            <span className="text-[11px] text-muted-foreground">Nom de l'établissement (des adresses sont proposées)</span>
            <AddressAutocomplete
              value={manual.name}
              onChange={(txt) => { setManual((m) => ({ ...m, name: txt })); setManualCoords(null); }}
              onSelect={(r) => {
                setManual({ name: (r.name || r.label.split(",")[0]).trim(), address: r.label });
                setManualCoords({ lat: r.lat, lon: r.lon });
              }}
              placeholder="Ex : Clinique du Parc, Hôpital…"
              testid="estab-manual-name"
            />
          </div>
          <div>
            <span className="text-[11px] text-muted-foreground">Adresse complète (modifiable, sans limite de distance)</span>
            <Input placeholder="N°, rue, code postal, ville" value={manual.address} onChange={(e) => { setManual({ ...manual, address: e.target.value }); setManualCoords(null); }} data-testid="estab-manual-address" />
          </div>
          <Button onClick={addManual} disabled={busy} data-testid="estab-manual-add">Ajouter</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Si vous ne choisissez pas de proposition, l'adresse saisie est géolocalisée automatiquement.</p>
      </div>

      {/* Liste enregistrée */}
      <div className="space-y-2 border-t border-border pt-4">
        <Label className="text-xs font-medium">Mes établissements ({list.length})</Label>
        {list.length === 0 && <p className="text-sm text-muted-foreground">Aucun établissement enregistré. Recherchez autour de vous ou ajoutez-en un manuellement.</p>}
        <div className="space-y-1" data-testid="estab-list">
          {list.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <span className="flex items-center gap-2 min-w-0"><MapPin className="h-3.5 w-3.5 text-primary shrink-0" /><span className="truncate"><b>{e.name}</b> <span className="text-muted-foreground text-xs">· {e.kind}{e.address && e.address !== e.name ? ` · ${e.address}` : ""}</span></span></span>
              <span className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" title={e.favorite ? "Favori (pré-sélectionné dans Jeregul)" : "Définir comme favori"} onClick={() => toggleFav(e)} data-testid={`estab-fav-${e.id}`}>
                  <Star className={`h-3.5 w-3.5 ${e.favorite ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`estab-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
